import { shouldEnableConsoleLogs, isDevelopment } from '../../config/appConfig';

/**
 * Admin Console Service
 * Manages console log visibility based on user role and environment settings
 */
class AdminConsole {
  private static instance: AdminConsole;
  private isAdmin: boolean = false;
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    info: typeof console.info;
    debug: typeof console.debug;
    error: typeof console.error;
  };
  private isInitialized: boolean = false;

  private constructor() {
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
      error: console.error.bind(console),
    };
  }

  static getInstance(): AdminConsole {
    if (!AdminConsole.instance) {
      AdminConsole.instance = new AdminConsole();
    }
    return AdminConsole.instance;
  }

  /**
   * Initialize console restrictions based on environment settings
   */
  init(): void {
    if (this.isInitialized) return;
    
    this.updateConsoleAccess();
    this.isInitialized = true;
  }

  /**
   * Update admin status and refresh console access
   * @param isAdmin - Whether the current user is an admin
   */
  setAdminStatus(isAdmin: boolean): void {
    this.isAdmin = isAdmin;
    this.updateConsoleAccess();
    
    // Debug logging to help troubleshoot
    if (isDevelopment() || shouldEnableConsoleLogs()) {
      const status = this.getStatus();
      console.info('AdminConsole status updated:', status);
    }
  }

  /**
   * Update console access based on current settings
   * Rules:
   * - Development: Always allow console logs (regardless of environment variable)
   * - Production with console logs enabled: Allow for admins only
   * - Production with console logs disabled: Block for everyone except critical errors
   */
  private updateConsoleAccess(): void {
    const isDev = isDevelopment();
    const consoleEnabled = shouldEnableConsoleLogs();

    if (isDev) {
      // Development: Always restore full console access
      this.restoreConsole();
    } else if (consoleEnabled && this.isAdmin) {
      // Production with console enabled: Allow for admins
      this.restoreConsole();
    } else {
      // Production: Restrict console for non-admins or when disabled
      this.restrictConsole();
    }

    // Force a console test to verify the change took effect
    if (isDev || (consoleEnabled && this.isAdmin)) {
      // Use original console to bypass any restrictions for this debug message
      this.originalConsole.info(`Console access ${isDev || (consoleEnabled && this.isAdmin) ? 'ENABLED' : 'DISABLED'} - isDev: ${isDev}, consoleEnabled: ${consoleEnabled}, isAdmin: ${this.isAdmin}`);
    }
  }

  /**
   * Restore full console functionality
   */
  private restoreConsole(): void {
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;
    // Always keep error functionality
    console.error = this.originalConsole.error;
  }

  /**
   * Restrict console functionality (keep errors for critical issues)
   */
  private restrictConsole(): void {
    console.log = () => {};
    console.warn = () => {};
    console.info = () => {};
    console.debug = () => {};
    // Always keep error functionality for critical issues
    console.error = this.originalConsole.error;
  }

  /**
   * Get current console access status
   */
  getStatus(): {
    isAdmin: boolean;
    isDevelopment: boolean;
    consoleEnabled: boolean;
    hasAccess: boolean;
  } {
    const isDev = isDevelopment();
    const consoleEnabled = shouldEnableConsoleLogs();
    const hasAccess = isDev || (consoleEnabled && this.isAdmin);

    return {
      isAdmin: this.isAdmin,
      isDevelopment: isDev,
      consoleEnabled,
      hasAccess,
    };
  }

  /**
   * Force enable console (for debugging purposes)
   * Should only be used in development or by admins
   */
  forceEnable(): void {
    if (isDevelopment() || this.isAdmin) {
      this.restoreConsole();
    }
  }

  /**
   * Force disable console (for testing purposes)
   */
  forceDisable(): void {
    this.restrictConsole();
  }
}

// Export singleton instance
export const adminConsole = AdminConsole.getInstance();

// Export class for testing purposes
export { AdminConsole };